import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryKeywords = {
    'platform-recommend': '成人短视频平台推荐,平台评测,平台排名',
    'content-classification': '成人短视频内容分类,短视频类型,内容指南',
    'safety-guide': '成人短视频安全观看,安全浏览技巧,安全防护',
    'privacy-protection': '成人短视频隐私保护,数据安全,匿名浏览',
    'legal-compliance': '成人短视频合规法规,法律解读,用户权益',
    'tech-tools': '成人短视频工具推荐,播放器,下载工具',
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${category.name} - 成人短视频指南`}
        description={category.description}
        keywords={categoryKeywords[slug as keyof typeof categoryKeywords] || category.name}
        canonicalUrl={`/category/${slug}`}
        ogImage={category.image}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="w-full lg:w-64 flex-shrink-0">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-4">
                  <i className={`${category.icon} text-2xl`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">{category.name}</h1>
                <p className="text-sm text-foreground-600 leading-relaxed">{category.description}</p>
                <p className="text-xs text-foreground-500 mt-3">{categoryArticles.length} 篇文章</p>
              </div>
              <div className="flex-1 w-full">
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-48 md:h-64 rounded-xl object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-3 left-3">
                        <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-xs text-foreground-700 rounded-full">
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span>{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                        <span>{article.author}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex flex-wrap gap-2">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mx-auto mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
                <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm mt-3 inline-block">返回首页</Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}